'use client';

import clsx from 'clsx';
import { RefObject } from 'react';
import { SelectOption, SelectProps } from '@/ui/select.types';

type SelectDropdownProps = {
    options: SelectOption[];
    multiple: boolean;
    value: string | undefined;
    selectedValueSet: Set<string>;
    highlightedIndex: number;
    usesModalPortal: boolean;
    portalPosition: { top: number; left: number; width: number };
    dropdownLayer: number;
    uiSize: NonNullable<SelectProps['uiSize']>;
    infoClassName?: string;
    listboxId: string;
    selectId: string;
    ariaLabel?: string;
    dropdownRef?: RefObject<HTMLDivElement | null>;
    onOptionSelect: (option: SelectOption) => void;
    onOptionHover: (index: number) => void;
};

const renderOptionInfo = (info: string, uiSize: NonNullable<SelectProps['uiSize']>, infoClassName?: string) => {
    const delimiter = ' - ';
    const delimiterIndex = info.indexOf(delimiter);
    const baseClassName = clsx(
        'block w-full min-w-0 whitespace-nowrap text-black/50',
        uiSize === 'default' && 'text-xs',
        uiSize === 'compact' && 'text-[10px]',
    );

    if (delimiterIndex === -1) {
        return <span className={clsx(baseClassName, infoClassName)}>{info}</span>;
    }

    const prefix = info.slice(0, delimiterIndex);
    const brand = info.slice(delimiterIndex + delimiter.length);

    return (
        <span className={baseClassName}>
            <span>{prefix}</span>
            <span className={clsx('inline-block align-bottom', infoClassName)}>{` - ${brand}`}</span>
        </span>
    );
};

export const SelectDropdown = ({
    options,
    multiple,
    value,
    selectedValueSet,
    highlightedIndex,
    usesModalPortal,
    portalPosition,
    dropdownLayer,
    uiSize,
    infoClassName,
    listboxId,
    selectId,
    ariaLabel,
    dropdownRef,
    onOptionSelect,
    onOptionHover,
}: SelectDropdownProps) => {
    return (
        <div
            ref={dropdownRef}
            data-click-outside-ignore="true"
            data-select-dropdown={selectId}
            className={clsx(
                'max-w-[90vw]',
                usesModalPortal ? 'pointer-events-auto fixed w-max' : 'absolute left-0 top-full mt-1 min-w-full w-max',
            )}
            style={
                usesModalPortal
                    ? {
                          top: portalPosition.top,
                          left: portalPosition.left,
                          minWidth: portalPosition.width,
                          zIndex: dropdownLayer,
                      }
                    : { zIndex: dropdownLayer }
            }
        >
            <div
                id={listboxId}
                role="listbox"
                aria-label={ariaLabel || 'Список вариантов'}
                aria-multiselectable={multiple || undefined}
                data-select-list={selectId}
                className={clsx(
                    'bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto',
                    uiSize === 'default' && 'py-1',
                    uiSize === 'compact' && 'py-0.5',
                )}
            >
                {options.length === 0 ? (
                    <div
                        className={clsx(
                            'text-gray-500 text-center',
                            uiSize === 'default' && 'px-4 py-1.5 text-sm',
                            uiSize === 'compact' && 'px-3 py-1 text-xs',
                        )}
                    >
                        Нет доступных вариантов
                    </div>
                ) : (
                    options.map((option, index) => {
                        const isSelected = multiple ? selectedValueSet.has(option.value) : option.value === value;

                        return (
                            <button
                                type="button"
                                key={option.value}
                                className={clsx(
                                    'w-full cursor-pointer transition-all duration-150 flex items-center justify-between',
                                    uiSize === 'default' && 'px-4 py-1.5 text-sm leading-5',
                                    uiSize === 'compact' && 'px-3 py-1 text-xs leading-4',
                                    index === highlightedIndex ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50',
                                    isSelected && 'bg-blue-50',
                                )}
                                onClick={() => onOptionSelect(option)}
                                onMouseEnter={() => onOptionHover(index)}
                                aria-pressed={isSelected}
                                data-select-option={option.value}
                                data-state={isSelected ? 'selected' : 'idle'}
                            >
                                <div className="flex w-full flex-col items-start text-start gap-0.5">
                                    <span className="text-gray-900">{option.name}</span>
                                    {option.info && renderOptionInfo(option.info, uiSize, infoClassName)}
                                </div>
                            </button>
                        );
                    })
                )}
            </div>
        </div>
    );
};
